<template>
  <custom-popup
    v-model="dialog"
    :cons="popupConfig"
    :loading="loading"
    max-width="1024"
    @close="closePopup"
    hide-confirm
    persistent
    :fullscreen="$vuetify.breakpoint.smAndDown"
    transition="dialog-bottom-transition"
  >
  <v-container fluid class="pa-0 pa-md-3">
    <template v-if="$vuetify.breakpoint.mdAndUp">
      <v-row align="start">
        <v-col cols="12" md="4" lg="5" class="d-flex flex-column sticky-preview">
          <v-card class="pa-4 flex-grow-1 d-flex flex-column align-center" outlined>
            <h3 class="mb-4 text-h6 font-weight-bold">پیش‌نمایش</h3>            
            <template v-if="selectedModel">
               <v-btn 
                block 
                class="mt-auto btn-get-code rounded-lg" 
                @click.stop.prevent="confirmAndEdit"
              >
                تأیید و استفاده از این الگو
                <v-icon right>mdi-check-circle-outline</v-icon>
              </v-btn>
              <div class="preview-container w-100 d-flex justify-center align-center flex-grow-1 my-4">
                <component
                  v-if="previewTheme"
                  :is="previewTheme"
                  v-model="previewData"
                  :data="previewData"
                  :orders.sync="previewOrders"
                />
              </div>
            </template>
            <template v-else>
              <div class="text-center text-muted mt-10 d-flex flex-column align-center justify-center h-100">
                <v-icon size="64" color="grey lighten-2">mdi-cellphone-dock</v-icon>
                <p class="mt-4 text-body-1">لطفاً یک الگو را از لیست انتخاب کنید تا پیش‌نمایش آن اینجا نمایش داده شود.</p>
              </div>
            </template>
          </v-card>
        </v-col>
        <!-- سمت چپ: لیست الگوها و فیلتر جستجو -->
        <v-col cols="12" md="8" lg="7">
          <v-card class="pa-4 h-100" outlined>
            <v-row align="center" class="mb-2">
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="searchQuery"
                  :items="categories"
                  clear-icon="mdi-close-circle-outline"
                  clearable
                  dense
                  item-text="title"
                  label="جستجو در الگوها..."
                  outlined
                  @keyup.enter="getData(1)"
                  @change="getData(1)"            
                      />
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="auto">
                <v-chip color="primary" outlined small>
                  {{ cards.length }} مورد بارگذاری شده
                </v-chip>
              </v-col>
            </v-row>

            <v-divider class="mb-6"></v-divider>

            <v-row v-if="loading && cards.length === 0" class="mt-5">
              <v-col cols="12" class="text-center">
                <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
              </v-col>
            </v-row>

            <v-row v-else-if="cards.length">
             <v-col 
                v-for="item in cards" 
                :key="item.id" 
                class="mb-6 pa-2"
                cols="12" sm="4" md="3" lg="3"
              >
                <v-card 
                  flat
                  class="position-relative"
                  :style="selectedModel && selectedModel.id === item.id ? 'border: 2px solid var(--v-success-base); border-radius: 15px;' : 'border: 2px solid transparent; border-radius: 15px;'"
                >
                  <v-avatar
                    v-if="selectedModel && selectedModel.id === item.id"
                    color="success"
                    size="28"
                    class="elevation-2 position-absolute"
                    style="z-index: 3; top: -10px; left: -10px;"
                  >
                    <v-icon dark size="18">mdi-check</v-icon>
                  </v-avatar>

                  <div
                    :style="{
                      background: `url(${getCardImage(item) || '/img/default.webp'})`,
                      'background-size': 'cover',
                      'background-position': 'center',
                      'background-repeat': 'no-repeat',
                      height: '14rem',
                    }"
                    class="card-style p-0 position-relative"
                    @click="selectForPreview(item)"
                  >
                    <div
                      class="position-absolute"
                      style="right: 5px; top: 5px; z-index: 2"
                    >
                      <img 
                        :src="getProfileImage(item) || '/media/cr_logo.webp'"
                        @error="onImageError"
                        alt=""
                        class="rounded-circle border border-2 img-fluid bg-white"
                        width="50"
                        height="50"
                        style="object-fit: cover;"
                      />
                    </div>
                  </div>
                  
                  <v-card-subtitle class="text-center pb-0">{{ item.title || item.qr?.data?.data?.visitName }}</v-card-subtitle>
                </v-card>
              </v-col>
            </v-row>

            <v-row v-else class="mt-5">
              <v-col cols="12" class="text-center">
                <v-icon size="50" color="grey lighten-1" class="mb-3">mdi-archive-cancel-outline</v-icon>
                <p class="text-muted">الگویی یافت نشد.</p>
              </v-col>
            </v-row>
            
            <!-- رفرنس برای Observer صفحه‌بندی -->
            <a id="dl" ref="dl"></a>
            <div v-if="loading && cards.length > 0" class="text-center w-100 my-4">
               <v-progress-circular indeterminate color="primary" size="30"></v-progress-circular>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <template v-else>
      <div class="mobile-layout pb-4">
        
        <v-sheet elevation="3" class="py-2 mb-3 bg-white">
          <v-slide-group
            v-if="cards.length > 0"
            class="pa-2"
            active-class="success"
            show-arrows
          >
            <v-slide-item v-for="item in cards" :key="item.id">
              <v-card
                class="ma-2 d-flex flex-column align-center justify-center text-center position-relative cursor-pointer"
                height="82"
                width="100"
                :style="{
                  border: (selectedModel && selectedModel.id === item.id) ? '2px solid var(--v-success-base)' : '1px solid #66646498',
                }"
                style="border-radius: 16px;"
                @click="selectForPreview(item)"
                elevation="0"
              >
                <!-- تیک انتخاب -->
                <v-avatar
                  v-if="selectedModel && selectedModel.id === item.id"
                  color="success"
                  size="22"
                  class="position-absolute"
                  style="top: -6px; left: -6px; z-index: 2;"
                >
                  <v-icon dark size="14">mdi-check</v-icon>
                </v-avatar>

                <!-- افزودن @error برای هندل کردن خطای 404 -->
                <img
                  :src="getProfileImage(item) || '/media/cr_logo.webp'"
                  @error="onImageError"
                  class="rounded-circle mb-1"
                  width="45"
                  height="45"
                  style="object-fit: cover; border: 3px solid #B4B4B759;"
                />
                
            <div class="text-caption font-weight-bold text-truncate w-100 px-1 primary--text" style="line-height: 1.2; text-shadow: 1px 1px 3px rgba(0,0,0,0.8);">
              {{ item.title || item.qr?.data?.data?.visitName ||'' }}
            </div>             
            </v-card>
            </v-slide-item>
            <v-slide-item>
              <div ref="dl" id="dl" class="d-flex align-center justify-center ma-2" style="width: 50px;">
                <v-progress-circular v-if="loading && next_page" indeterminate color="primary" size="24"></v-progress-circular>
              </div>
            </v-slide-item>
          </v-slide-group>
        </v-sheet>
        <div class="px-3 mb-4 d-flex align-center">
          <v-autocomplete
            v-model="searchQuery"
            :items="categories"
            clear-icon="mdi-close-circle-outline"
            clearable
            hide-details
            dense
            item-text="title"
            label="جستجو در الگوها..."
            outlined
            background-color="white"
            style="border-radius: 12px;"
            @keyup.enter="getData(1)"
            @change="getData(1)"         
             />
          <!-- دکمه جستجو -->
          <v-btn 
            class="mr-2 btn-show rounded-pill" 
            style="border-radius: 12px; height: 40px; min-width: 40px; " 
            elevation="1"
            @click="getData(undefined)"
          >
            <v-icon>mdi-magnify</v-icon>
          </v-btn>
        </div>
        <!-- پیش‌نمایش و دکمه -->
        <div class="preview-area px-3 d-flex flex-column align-center mb-10">
          <template v-if="selectedModel">
            <!-- <h4 class="mb-3 text-subtitle-1 font-weight-bold text-center">پیش‌نمایش الگو</h4> -->
            
            <v-btn 
              block 
              large 
              class="rounded-lg elevation-2 font-weight-bold mb-5 btn-get-code"
              @click.stop.prevent="confirmAndEdit"
            >
              تأیید و استفاده از این الگو
              <v-icon right>mdi-check-circle-outline</v-icon>
            </v-btn>

            <div class="w-100 d-flex justify-center">
              <component
                v-if="previewTheme"
                :is="previewTheme"
                v-model="previewData"
                :data="previewData"
                :orders.sync="previewOrders"
              />
            </div>
          </template>
          <template v-else>
            <div class="text-center text-muted mt-8">
              <v-icon size="48" color="grey lighten-2">mdi-gesture-swipe-horizontal</v-icon>
              <p class="mt-4 text-body-2">لطفاً برای مشاهده پیش‌نمایش، یک الگو را از نوار بالا انتخاب کنید.</p>
            </div>
          </template>
        </div>
      </div>
    </template>
  </v-container>
  </custom-popup>
</template>

<script>
import MobileViwer from "@/view/pages/qrland/components/mobileViwer";
import VisitThemeTwo from "@/view/pages/qrland/components/themes/visitThemeTwo";
import VisitThemeFlipped from "@/view/pages/qrland/components/themes/visitThemeFlipped";
import galleryMobile from "@/view/pages/otherQrTypes/giftGallery/components/galleryMobile";

export default {
  name: "modelsGallery",

  components: {
    MobileViwer,
    VisitThemeTwo,
    VisitThemeFlipped,
    galleryMobile
  },
 props: {
    value: {
      type: Boolean,
      default: false
    },
    iden: {
      type: [String, Number],
      default: ''
    },
    type: {
      type: String,
      default: ''
    },
    domain: {
      type: String,
      default: 'qrland.in'
    },
     isTransferMode: {
    type: Boolean,
    default: false
  }
   },
     data() {
    return {
        popupConfig:{
        title: "تکمیل فرایند انتقال/انتخاب الگوی آماده",
        icon: "select",
        buttons: []
      },
      categories: [],
      loading: false,
      cards: [], 
      searchQuery: '',
      observer: null,
      next_page: null,
      tableProps: {
        type: '',
      },
      selectedModel: null,
      previewTheme: 'MobileViwer',
      previewData: {},
      previewOrders: []
    }
  },
  computed: {
        dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }

  },
      currentType() {
      return this.type || this.$route?.query?.type || '';
    },
    currentIden() {
      return this.iden || this.$route?.query?.iden || '';
    },
    currentDomain() {
      return this.domain || this.$route?.query?.domain || '';
    }
  },
    watch: {
    value(val) {
      if (val) {
        this.selectedModel = null;
        this.searchQuery = '';
        this.getData(1);
      }
    }
  },

  mounted() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (
          entry.isIntersecting &&
          this.next_page !== null &&
          !this.loading
        ) {
          if (this.$refs.dl) this.observer.unobserve(this.$refs.dl);
          this.getData(this.next_page);
        }
      });
    });

    if (this.type) {
      this.tableProps.type = this.type;
    }
    this.getData(1); 
  },
  methods: {
    closePopup(){
        this.dialog= false;
        this.$emit('close');
    },
    onImageError(event) {
      event.target.src = '/media/cr_logo.webp';
    },
    onSearch() {
      this.getData(1);
    },

        getData(page = 1) {
      this.loading = true;
      
      if (page === 1) {
        this.cards = [];
      }

      this.$api
        .get(`/qr/template/list`, {
          params: {
            type: this.tableProps.type, 
            page: page,
            search: this.searchQuery,
          },
        })
        .then(({ data }) => {
          this.categories = data.data.categories;
          
          this.cards = [...this.cards, ...data.data.data];
          
          if (data.data.has_next_page) {
            this.next_page = data.data.next_page;
            
            this.$nextTick(() => {
              if (this.$refs.dl) {
                this.observer.observe(this.$refs.dl);
              }
            });
          } else {
            this.next_page = null;
          }
        })
        .catch((reason) => {
          this.$modal.error(this.$t("error"), this.$t("data_fetch_error"));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getCardImage(item) {
      try {
        let rawData = item.data; 
        if (item.qr && item.qr.data) rawData = item.qr.data;
        const parsed = typeof rawData === 'string' ? JSON.parse(rawData) : (rawData || {});
        const d = parsed.data || parsed;
        
        if (d?.photo?.headBackground) return d.photo.headBackground;
        if (d?.gallery?.[0]?.images?.[0]?.url) return d.gallery[0].images[0].url;
        if (d?.banner?.gallery?.[0]?.images?.[0]?.url) return d.banner.gallery[0].images[0].url;

        return item.image || null;
      } catch (e) {
        return item.image || null;
      }
    },

    getProfileImage(item) {
      try {
        let rawData = item.data; 
        if (item.qr && item.qr.data) rawData = item.qr.data;
        const parsed = typeof rawData === 'string' ? JSON.parse(rawData) : (rawData || {});
        const d = parsed.data || parsed;
        
        return (d.photo && d.photo.profile) || item.profileImage || null;
      } catch (e) {
        return item.profileImage || null;
      }
    },

    selectForPreview(item) {
      this.selectedModel = item;
      try {
        let rawData = item.data; 
        if (item.qr && item.qr.data) {
          rawData = item.qr.data;
        }
        const parsedData = typeof rawData === 'string' ? JSON.parse(rawData) : (rawData || {});
        this.previewTheme = this.getPreviewTheme(item.type, parsedData.theme);
        this.previewData = parsedData.data || parsedData; 
        this.previewOrders = parsedData.orders || [];
        
      } catch (e) {
        this.previewTheme = this.getPreviewTheme(item.type,'');
        this.previewData = {};
        this.previewOrders = [];
      }
    },
    getPreviewTheme(type, savedTheme) {
      if (!type) return savedTheme || 'MobileViwer';
      const typeLower = type.toLowerCase();
      const themeMap = {
        visitcard: {
          default: 'VisitThemeTwo',
          allowed: ['MobileViwer', 'VisitThemeTwo', 'VisitThemeFlipped']
        },
        gift: {
          default: 'galleryMobile',
          allowed: ['galleryMobile']
        },
        menu: {
          default: 'MobileViwer',
          allowed: ['MobileViwer']
        }
      };
      const mapping = themeMap[typeLower];
      if (!mapping) {
        return savedTheme || 'MobileViwer';
      }
      if (savedTheme && mapping.allowed.includes(savedTheme)) {
        return savedTheme;
      }
      return mapping.default;
    },
    confirmAndEdit() {
      if (this.selectedModel) {
        let targetData = this.selectedModel.qr ? this.selectedModel.qr.data : this.selectedModel.data;
        let targetQr = this.selectedModel.qr? this.selectedModel.qr.qr : this.selectedModel.qr;
        let payload = {
          data: typeof targetData === 'string' ? targetData : JSON.stringify(targetData),
          // qr: typeof targetQr === 'string' ? targetQr : JSON.stringify(targetQr),
        };
    if (this.isTransferMode) {
      this.$emit('select-template', payload);
    } 
      } }}}
</script>
<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.sticky-preview {
  position: -webkit-sticky; 
  position: sticky;
  top: 85px;
  z-index: 10;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
}
.card-style {
  cursor: pointer;
  border-radius: 15px;
  background-size: cover !important;
  background-position: center !important;
  background-repeat: no-repeat !important;
  height: 14rem;
  overflow: hidden;
}
.position-relative {
  position: relative !important;
}
.position-absolute {
  position: absolute !important;
}
.fixed-bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  box-shadow: 0 -4px 15px rgba(0, 0, 0, 0.08);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}
.mobile-layout {
  min-height: calc(100vh - 60px);
}
</style>
